import { useCallback, useEffect, useState } from 'react';
import apiClient from '../services/api';
import { logger } from '../utils/logger';

export interface BackendConnectionStatus {
  isConnected: boolean;
  isChecking: boolean;
  lastChecked: Date | null;
  error: string | null;
}

interface UseBackendConnectionReturn extends BackendConnectionStatus {
  checkConnection: () => Promise<void>;
  clearError: () => void;
}

/**
 * Hook to monitor backend connectivity status
 * Provides real-time connection status and error reporting
 */
export const useBackendConnection = (
  autoCheck: boolean = true,
  checkInterval: number = 300000 // 300 seconds
): UseBackendConnectionReturn => {
  const [status, setStatus] = useState<BackendConnectionStatus>({
    isConnected: false,
    isChecking: false,
    lastChecked: null,
    error: null,
  });

  const checkConnection = useCallback(async (): Promise<void> => {
    setStatus(prev => ({ ...prev, isChecking: true, error: null }));

    try {
      const response = await apiClient.healthCheck();

      if (response.success) {
        setStatus({
          isConnected: true,
          isChecking: false,
          lastChecked: new Date(),
          error: null,
        });
      } else {
        setStatus({
          isConnected: false,
          isChecking: false,
          lastChecked: new Date(),
          error: response.error || 'Backend health check failed',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown connection error';

      setStatus({
        isConnected: false,
        isChecking: false,
        lastChecked: new Date(),
        error: `Cannot reach backend server: ${errorMessage}`,
      });
    }
  }, []);

  const clearError = useCallback((): void => {
    setStatus(prev => ({ ...prev, error: null }));
  }, []);

  // Initial connection check
  useEffect(() => {
    if (autoCheck) {
      checkConnection();
    }
  }, [autoCheck, checkConnection]);

  // Periodic connection checks
  useEffect(() => {
    if (!autoCheck || !checkInterval) return;

    const interval = setInterval(() => {
      // Only check if not currently checking
      if (!status.isChecking) {
        checkConnection();
      }
    }, checkInterval);

    return () => clearInterval(interval);
  }, [autoCheck, checkInterval, checkConnection, status.isChecking]);

  // Listen for network events
  useEffect(() => {
    const handleOnline = () => {
      logger.info('Network came back online, checking backend connection...');
      if (!status.isChecking) {
        checkConnection();
      }
    };

    const handleOffline = () => {
      logger.info('Network went offline');
      setStatus(prev => ({
        ...prev,
        isConnected: false,
        error: 'Network connection lost',
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkConnection, status.isChecking]);

  return {
    ...status,
    checkConnection,
    clearError,
  };
};
